/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-cycle */
import {
  useForm,
  FormProvider,
  SubmitHandler,
  SubmitErrorHandler,
  Control,
  Controller,
  UseFormReturn,
  useWatch
} from 'react-hook-form'
import { ZodEffects, ZodObject, ZodTypeAny } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {
  CSSProperties,
  Dispatch,
  Fragment,
  ReactElement,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import CheckBox from '@/shared/components/CheckBox/CheckBox'
import {
  ErrorModel,
  OptionType,
  RawOptionType
} from '@/shared/api/BaseAPI.model'
import { buildUnifiedValidationSchema } from '@/features/menu-management/helpers/buildUnifiedValidationSchema'
import { set, get } from 'lodash'
import Alert from '@/shared/components/Alert/Alert'
import OverlayScrollbar from '@/shared/components/OverlayScrollbar/OverlayScrollbar'
import { useTranslation } from 'react-i18next'
import Stepper from '@/shared/components/Stepper/Stepper'
import ImageUploadButton from '@/shared/components/ImageUploadButton/ImageUploadButton'
import { UseQueryResult } from '@tanstack/react-query'
import TagInput from '@/shared/components/TagInput/TagInput'
import ConditionalField from '@/shared/components/ConditionalField/ConditionalField'
import { GridColDef, GridRowsProp } from '@mui/x-data-grid-pro'
import TextField from '../TextField/TextField'
import styles from './DynamicFrom.module.scss'
import Select from '../Select/Select'
import IMAGE_SLOTS_KEYS from '@/shared/store/imageSlots.model'
import { TABLE_KEYS } from '../Table/models/Filter.model'
import { DynamicFormTableField } from '../DynamicFromTableField/DynamicFromTableField'
import Rating from '../Rating/Rating'
import TimePicker from '../TimePicker/TimePicker'
import Tabs from '../Tabs/Tabs'
import CheckBoxGroup from '../CheckBoxGroup/CheckBoxGroup'

// Parsing "08:00" needs the plugin; without it dayjs falls back to Date and
// returns Invalid Date for a bare time.
dayjs.extend(customParseFormat)

/** Wire and form-state format for a `time` field. */
export const TIME_VALUE_FORMAT = 'HH:mm'

export type FieldType =
  | 'text'
  | 'password'
  | 'select'
  | 'email'
  | 'multi-select'
  | 'text-area'
  | 'checkbox'
  | 'table'
  | 'custom'
  // A value shown and never edited (an id, a date): its label over plain
  // text, because a greyed-out field looks like something you could unlock.
  | 'read-only'
export type TextFieldType =
  'text' | 'password' | 'email' | 'text-area' | 'number'

export type selectValueType = OptionType | OptionType[] | null
export type textFieldValueType = string | null
export type checkboxValueType = boolean

export type referenceTypes = [
  (
    | 'watchOptions'
    | 'disableIfEmpty'
    | 'disableIfNotEmpty'
    | 'lockReferenceValue'
  ),

  ...Array<'watchOptions' | 'disableIfEmpty' | 'disableIfNotEmpty'>
]

export type visibilityLogic = 'visibleIfEmpty' | 'visibleIfNotEmpty'

export interface CoreFieldProps {
  key?: string
  name: string
  label?: string
  type: string
  placeholder?: string
  required?: boolean
  /** A muted line under a text field or a select that says what it is for. */
  hint?: string
  // Unit rendered inside the input, e.g. "zł" on a price field
  endAdornment?: ReactNode
  /**
   * `number` fields only: strips anything that is not a digit as it is typed and
   * floors the result at one. Declared by the reward form and the print grid,
   * and it has to be forwarded below or the config says something the input
   * never does.
   */
  onlyInteger?: boolean
}

export interface LayoutProps {
  /**
   * A field's share of its `input-group` row; 0 keeps it at its own width,
   * as a picture beside the fields it belongs to.
   */
  flexGrow?: number
  inputGroupDirection?: 'row' | 'column'
  /**
   * `input-group` only: lays its fields on this many equal columns, so the
   * rows of a long form line up. Each field takes one unless it has `span`.
   */
  gridColumns?: number
  /** Inside a `gridColumns` group: how many columns the field takes. */
  span?: 2 | 3 | 'all'
  /** `input-group` only: a title over the group, as a section of the form. */
  heading?: string
  /** `text-area` only: its height in lines; five by default. */
  lines?: number
  /**
   * Scrolls a column `input-group` inside its own box instead of letting it grow.
   * For a group whose length is data-driven — one field per language the venue
   * has enabled — where the form otherwise outgrows any dialog it sits in.
   */
  scrollable?: boolean
  alignItems?: 'flex-start' | 'flex-end' | 'center' | 'stretch'
  focused?: boolean
  maxHeight?: string
  hideClearIcon?: boolean
  /** Multi-select only: shrink the chips to fit rather than clip the last one. */
  fitTags?: boolean
  disabled?: boolean
}

export interface ValidationProps {
  validation?: ZodTypeAny
  defaultValue?: string | boolean | OptionType | OptionType[] | null
}

export type ReferenceType =
  | 'watchOptions'
  | 'disableIfEmpty'
  | 'disableIfNotEmpty'
  // Whatever the referenced field currently holds must stay selected in this
  // one. For a pair where one field picks out of the other's selection, and
  // dropping the pick leaves the form in a state it cannot be saved from.
  | 'lockReferenceValue'

export interface ReferenceProps {
  referenceField?: string
  referenceTypes?: ReferenceType[]
}

export interface SyncProps {
  syncFormField?: string
  isSyncFormField?: boolean
  availableOptions?: OptionType[]
}

export type VisibilityLogic = 'visibleIfEmpty' | 'visibleIfNotEmpty'

export interface VisibilityProps {
  visibilityLogic?: VisibilityLogic
}

export interface MediaProps {
  imageQuery?: UseQueryResult<any, ErrorModel>
  modalId?: string
  imageShape?: 'circle' | 'rectangle'
  // Dropping an image that is also stored server-side is the owner's call, the
  // form only knows about the slot the user just cropped into.
  onImageDelete?: () => void
}

export interface OptionProps {
  options?: OptionType[]
  rawOptions?: RawOptionType[]
  inputs?: FormFieldConfig[]
}

export interface EventProps {
  onChange?: (
    value: string | OptionType | number | boolean,
    index?: number
  ) => void
  onChangeValue?: any
}

export interface TableProps {
  columns?: GridColDef[]
  tableKey?: TABLE_KEYS
  tableDefaultValue?: GridRowsProp
  editable?: boolean
  dataMatrixFields?: string[]
  columnVisibilityModel?: Record<string, boolean>
  columnVisibilityRules?: Array<{
    columnField: string
    dependsOn: string
    hideWhenEmpty?: boolean
    hideWhenNotEmpty?: boolean
  }>
}

export interface TimeProps {
  /** Granularity of the minutes column; 15 by default. */
  minutesStep?: number
}

export interface RatingProps {
  icon?: ReactNode
  max?: number
  precision?: number
  label?: string
}

type TabObject = {
  label: string
  value: number | string
}

export interface TabsProps {
  tabs?: TabObject[]
}

export interface ActionProps {
  // Action node aligned to the right edge of the form. On a `tabs` field it is
  // rendered on the same row as the tabs; as a standalone `action` field it
}

export interface CheckBoxGroupProps {
  checkBoxes?: Array<{
    selected: boolean
    label: string
  }>
  checkBoxGroupOnChange?: (index: number, value: boolean) => void
}

export interface FormFieldConfig
  extends
    CoreFieldProps,
    LayoutProps,
    ValidationProps,
    ReferenceProps,
    SyncProps,
    VisibilityProps,
    MediaProps,
    OptionProps,
    TableProps,
    EventProps,
    RatingProps,
    TimeProps,
    CheckBoxGroupProps,
    TabsProps,
    ActionProps {
  /**
   * `custom` only: what the form has no field type for, rendered where the
   * config puts it. It is handed the form, so it can read and set values
   * (`useController` on `form.control` for a value of its own).
   */
  render?: (form: UseFormReturn<any>) => ReactNode
}

export interface StepFieldConfig {
  type: 'step'
  name: string
  label: string
  fields: FormFieldConfig[]
  referenceField?: string
  visibilityLogic?: VisibilityLogic
}

interface buttonContainerConfigProps {
  submitButton: {
    text: string
    maxWidth: boolean
    hidden?: boolean
  }
  cancelButton: {
    text: string
    maxWidth: boolean
    hidden?: boolean
  }
}

export interface DynamicFormProps {
  id: string
  fields: FormFieldConfig[]
  onSubmit?: SubmitHandler<Record<string, any>>
  validationSchema?: ZodObject<any> | ZodEffects<ZodObject<any>>
  setIsDirty?: (value: boolean) => void
  condensed?: boolean
  superCondensed?: boolean
  /**
   * Sets the top-level fields apart as sections of a long form: more room
   * between them, and none for one that renders nothing.
   */
  sectioned?: boolean
  onIsDirtyChange?: (isDirty: boolean) => void
  /** Called with the errors when a submit is refused, after they are shown. */
  onInvalid?: SubmitErrorHandler<Record<string, any>>
  /**
   * Turns an error message into the words shown, for a schema that raises
   * codes rather than sentences. Messages are shown as they are without it.
   */
  errorMessage?: (message: string) => string
  isLoadingData?: boolean
  defaultValues?: Record<string, any>
  buttonContainerConfig?: buttonContainerConfigProps
  formSuccessMessage?: string | ReactNode
  formErrorMessage?: string | ReactNode
  resetOnSubmit?: boolean
  setSteps?: Dispatch<SetStateAction<StepFieldConfig[] | null>>
  setActiveStep?: Dispatch<SetStateAction<number>>
  activeStep?: number
  setVisibleStepsCount?: Dispatch<SetStateAction<number>>
  FormEffects?: ReactNode
  /**
   * Rendered last, inside the form and inside the `FormProvider`. This is where
   * a submit/cancel row belongs when any control beside it needs form context,
   * which a button outside the provider cannot get.
   */
  FormActions?: ReactNode
  validationMode?: 'onChange' | 'onBlur' | 'onSubmit' | 'onTouched' | 'all'
}

const isStepField = (field: FormFieldConfig): field is StepFieldConfig =>
  field.type === 'step' && 'fields' in field

const joinPath = (
  parentPath: string,
  field: FormFieldConfig | StepFieldConfig
): string => {
  if (field.type === 'step') return parentPath

  const { name } = field
  if (name) {
    return parentPath ? `${parentPath}.${name}` : name
  }
  return parentPath
}

export const findFirstFieldPath = (
  fields: (FormFieldConfig | StepFieldConfig)[],
  predicate: (field: FormFieldConfig) => boolean,
  parentPath = ''
): string | null =>
  fields.reduce<string | null>((found, field) => {
    if (found) return found

    const fullPath = joinPath(parentPath, field)

    if (predicate(field)) return fullPath

    if (field.type === 'input-group' && field.inputs) {
      return findFirstFieldPath(field.inputs, predicate, fullPath)
    }

    if (isStepField(field)) {
      return findFirstFieldPath(field.fields, predicate, fullPath)
    }

    return null
  }, null)

export const findAllFieldsPaths = (
  fields: (FormFieldConfig | StepFieldConfig)[],
  predicate: (field: FormFieldConfig) => boolean,
  parentPath = ''
): string[] =>
  fields.flatMap(field => {
    const fullPath = joinPath(parentPath, field)
    const matches = predicate(field) ? [fullPath] : []

    if (field.type === 'input-group' && field.inputs) {
      return [
        ...matches,
        ...findAllFieldsPaths(field.inputs, predicate, fullPath)
      ]
    }

    if (isStepField(field)) {
      return [
        ...matches,
        ...findAllFieldsPaths(field.fields, predicate, fullPath)
      ]
    }

    return matches
  })

export const findFieldByPath = (
  fields: (FormFieldConfig | StepFieldConfig)[],
  targetPath: string,
  parentPath = ''
): FormFieldConfig | null =>
  fields.reduce<FormFieldConfig | null>((found, field) => {
    if (found) return found

    const fullPath = joinPath(parentPath, field)
    if (fullPath === targetPath) return field

    if (field.type === 'input-group' && field.inputs) {
      return findFieldByPath(field.inputs, targetPath, fullPath)
    }

    if (isStepField(field)) {
      return findFieldByPath(field.fields, targetPath, fullPath)
    }

    return null
  }, null)

export const buildInitialDefaults = (
  fields: (FormFieldConfig | StepFieldConfig)[],
  parentPath = ''
): Record<string, any> =>
  fields.reduce<Record<string, any>>((defaults, field) => {
    const fullPath = joinPath(parentPath, field)

    if (field.type === 'input-group' && field.inputs) {
      const nested = buildInitialDefaults(field.inputs, fullPath)
      return { ...defaults, ...nested }
    }

    if (isStepField(field)) {
      const nested = buildInitialDefaults(field.fields, fullPath)
      return { ...defaults, ...nested }
    }

    if (field.type === 'custom') return defaults

    let emptyValue: any = ''
    if (field.type === 'multi-select') {
      emptyValue = []
    } else if (field.type === 'select') {
      emptyValue = null
    } else if (field.type === 'checkbox') {
      emptyValue = false
    } else if (field.type === 'tagInputsList') {
      emptyValue = {}
    } else if (field.type === 'tabs') {
      const first = field.tabs?.[0]
      emptyValue = first
        ? ({ label: first.label, value: first.value } as OptionType)
        : null
    }

    const next = { ...defaults }
    set(next, fullPath, emptyValue)
    return next
  }, {})

/** A `read-only` field: watched, so a reset of the form shows the new value. */
function ReadOnlyValue({
  control,
  name,
  label,
  endAdornment
}: {
  control: Control<any>
  name: string
  label?: string
  endAdornment?: ReactNode
}) {
  const value = useWatch({ control, name })

  return (
    <div className={styles.readOnlyField}>
      {label && (
        <p className={`label-bold-medium ${styles.readOnlyLabel}`}>{label}</p>
      )}
      <div className={styles.readOnlyValue}>
        <span className="body-medium">{value}</span>
        {endAdornment}
      </div>
    </div>
  )
}

/**
 * A scrolling `input-group` with the bar drawn over it rather than beside it.
 *
 * The native `::-webkit-scrollbar` takes layout width on most platforms, and this
 * one widens on hover, so it narrowed the fields by 4px the moment the pointer
 * reached it. OverlayScrollbar is absolutely positioned, so the fields keep the
 * full width whatever the bar does.
 *
 * Its own component because the bar needs a ref to the scroller, and `renderField`
 * is a plain function called during render, where a hook cannot go.
 *
 * The fields make room for the bar only while it is shown: a group of two
 * languages has nothing to scroll, and its fields stood narrower than the
 * ones above them for a bar that never appeared.
 */
function ScrollableInputGroup({
  className,
  style,
  children
}: {
  className: string
  style?: CSSProperties
  children: ReactNode
}) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [isScrollable, setIsScrollable] = useState(false)

  return (
    <div className={styles.scrollableGroupShell}>
      <div
        ref={scrollerRef}
        className={`${className} ${isScrollable ? styles.isScrollable : ''}`}
        style={style}
      >
        {children}
      </div>
      <OverlayScrollbar
        targetRef={scrollerRef}
        onScrollableChange={setIsScrollable}
      />
    </div>
  )
}

function DynamicForm(props: DynamicFormProps) {
  const {
    id,
    fields,
    onSubmit,
    validationSchema,
    setIsDirty,
    condensed,
    superCondensed,
    sectioned = false,
    onIsDirtyChange,
    onInvalid,
    errorMessage,
    defaultValues,
    formErrorMessage,
    formSuccessMessage,
    resetOnSubmit = false,
    setActiveStep,
    setSteps,
    activeStep = 0,
    setVisibleStepsCount,
    FormEffects,
    FormActions,
    validationMode = 'onChange'
  } = props

  const { t } = useTranslation()
  const [isSubmitted, setIsSubmitted] = useState(false)

  const steps = useMemo(
    () => fields.filter(f => f.type === 'step') as StepFieldConfig[],
    [fields]
  )

  const isStepField = (field: FormFieldConfig): field is StepFieldConfig =>
    field.type === 'step'

  const allFormFields = useMemo(() => {
    const flattenFields = (fields: FormFieldConfig[]): FormFieldConfig[] =>
      fields.flatMap(f => (isStepField(f) ? flattenFields(f.fields) : [f]))

    return flattenFields(fields)
  }, [fields])

  const schema = validationSchema || buildUnifiedValidationSchema(allFormFields)

  const initialDefaults = buildInitialDefaults(allFormFields)

  const methods = useForm({
    resolver: zodResolver(schema),
    mode: validationMode,
    criteriaMode: 'all',
    defaultValues: defaultValues ?? initialDefaults
  })

  const stepReferenceFields = useMemo(
    () =>
      steps
        .filter(step => step.referenceField)
        .map(step => step.referenceField as string),
    [steps]
  )

  const watchedStepReferences = useWatch({
    control: methods.control,
    name: stepReferenceFields
  })

  const visibleSteps = useMemo(
    () =>
      steps.filter(step => {
        if (!step.referenceField || !step.visibilityLogic) return true

        const refIndex = stepReferenceFields.findIndex(
          name => name === step.referenceField
        )

        const refValue =
          Array.isArray(watchedStepReferences) && refIndex >= 0
            ? watchedStepReferences[refIndex]
            : methods.getValues(step.referenceField)

        if (step.visibilityLogic === 'visibleIfEmpty') {
          return !refValue || (Array.isArray(refValue) && refValue.length === 0)
        }

        if (step.visibilityLogic === 'visibleIfNotEmpty') {
          return !!refValue && (!Array.isArray(refValue) || refValue.length > 0)
        }

        return true
      }),
    [steps, watchedStepReferences, stepReferenceFields, methods]
  )

  /*
    The visible ones, not all of them. `activeStep` is an index into what the
    stepper shows, so a consumer that reads `steps[activeStep]` — the details
    modal does, to decide what belongs in its action row — would look at the
    wrong step as soon as one is hidden by its reference field.
  */
  useEffect(() => {
    if (setSteps) {
      setSteps(visibleSteps)
    }
  }, [visibleSteps, setSteps])

  useEffect(() => {
    if (setVisibleStepsCount) {
      setVisibleStepsCount(visibleSteps.length)
    }
  }, [visibleSteps, setVisibleStepsCount])

  useEffect(() => {
    if (activeStep >= visibleSteps.length && setActiveStep) {
      setActiveStep(visibleSteps.length - 1)
    }
  }, [visibleSteps, activeStep, setActiveStep])

  const currentStepFields =
    visibleSteps.length > 0 ? visibleSteps[activeStep]?.fields || [] : fields

  /*
    A group that scrolls has to be the thing that gives way when the dialog runs
    out of room, otherwise the dialog itself starts scrolling and the user gets a
    second scrollbar around the first. Only forms that actually contain one opt
    into the height chain below, so the other six users of this component are
    left exactly as they were.
  */
  const hasScrollableField = currentStepFields.some(field => field.scrollable)

  const [isCondensed, setIsCondensed] = useState(false)
  const [formErrorMessageState, setFormErrorMessageState] = useState<
    string | ReactNode | null
  >(null)
  const [showFormSuccess, setShowFormSuccess] = useState(false)

  const [mappedSyncFieldsOptions, setMappedSyncFieldsOptions] = useState<{
    [name: string]: OptionType[]
  }>({})

  const { handleSubmit, control, formState, reset } = methods

  const hasStepError = useMemo(
    () =>
      steps.map(step => {
        const names = findAllFieldsPaths(step.fields, () => true)
        return names.some(name => !!get(formState.errors, name))
      }),
    [formState.errors, steps]
  )

  useEffect(() => {
    if (defaultValues) reset(defaultValues)
  }, [defaultValues, reset])

  // Find all tabs field names to exclude from dirty tracking
  const tabsFieldNames = useMemo(
    () => findAllFieldsPaths(fields, f => f.type === 'tabs'),
    [fields]
  )

  // Serialize dirtyFields keys to detect all changes
  const dirtyFieldsKey = JSON.stringify(
    Object.keys(formState.dirtyFields).sort()
  )

  useEffect(() => {
    if (setIsDirty) {
      // Exclude tabs fields from dirty check (language selector shouldn't trigger confirmation modal)
      const dirtyFieldNames = Object.keys(formState.dirtyFields)
      const relevantDirtyFields = dirtyFieldNames.filter(
        name => !tabsFieldNames.includes(name)
      )
      setIsDirty(relevantDirtyFields.length > 0)
    }
  }, [dirtyFieldsKey, formState.dirtyFields, setIsDirty, tabsFieldNames])

  useEffect(() => {
    setIsCondensed(true)
  }, [condensed, superCondensed])

  useEffect(() => {
    onIsDirtyChange?.(formState.isDirty)
  }, [formState.isDirty, onIsDirtyChange])

  const fieldsWithSyncFormField = useMemo(
    () => findAllFieldsPaths(fields, f => !!f.syncFormField),
    [fields]
  )

  const watchedSyncFields = useWatch({
    control,
    name: fieldsWithSyncFormField
  })

  const isFormTranslating = useWatch({ control, name: '_isTranslating' })

  const syncFormFields = useMemo(
    () => findAllFieldsPaths(fields, f => f.isSyncFormField === true),
    [fields]
  )

  const mappedSyncFieldsValues = useMemo(
    () =>
      fieldsWithSyncFormField.reduce(
        (acc, name, index) => {
          acc[name] = watchedSyncFields[index]
          return acc
        },
        {} as Record<string, any>
      ),
    [fieldsWithSyncFormField, watchedSyncFields]
  )

  const watchedSyncFormFields = useWatch({
    control,
    name: syncFormFields
  })

  const mappedSyncFormFieldsValues = useMemo(
    () =>
      syncFormFields.reduce(
        (acc, name, index) => {
          acc[name] = watchedSyncFormFields[index]
          return acc
        },
        {} as Record<string, any>
      ),
    [syncFormFields, watchedSyncFormFields]
  )

  const fieldsWithSyncData = useMemo(
    () => findAllFieldsPaths(fields, f => !!f.syncFormField),
    [fields]
  )

  const fieldsWithReferencesNames = useMemo(
    () =>
      findAllFieldsPaths(fields, f => !!f.referenceField && !f.syncFormField),
    [fields]
  )

  const fieldsWithReferenceConfig = useMemo(
    () =>
      fieldsWithReferencesNames
        ? fieldsWithReferencesNames.map(
            fieldWithReferenceName =>
              findFieldByPath(fields, fieldWithReferenceName)?.referenceField
          )
        : null,
    [fieldsWithReferencesNames, fields]
  )

  const watchedReferenceFields = useWatch({
    control,
    name:
      fieldsWithReferenceConfig?.filter(
        (name): name is string => name !== undefined
      ) || []
  })

  const mappedReferencedFieldsValues = useMemo(
    () =>
      fieldsWithReferenceConfig?.reduce(
        (acc, name, index) => {
          if (name) {
            acc[name] = watchedReferenceFields[index]
          }
          return acc
        },
        {} as Record<string, any>
      ),
    [fieldsWithReferenceConfig, watchedReferenceFields]
  )

  useEffect(() => {
    const languageCodes = new Set<string>()
    Object.values(mappedSyncFieldsValues).forEach(value => {
      if (value && typeof value === 'object') {
        Object.keys(value).forEach(lang => languageCodes.add(lang))
      }
    })

    const incompleteLangs = new Set<string>()
    languageCodes.forEach(lang => {
      const allFieldsFilled = Object.values(mappedSyncFieldsValues).every(
        field => {
          if (field && typeof field === 'object' && lang in field) {
            const val = field[lang]
            return val !== null && val !== undefined && val !== ''
          }
          return true
        }
      )
      if (!allFieldsFilled) incompleteLangs.add(lang)
    })

    const newSyncOptions = Object.entries(mappedSyncFieldsOptions).reduce(
      (acc, [key, options]) => {
        acc[key] = options.map(opt => ({
          ...opt,
          icon: incompleteLangs.has(opt.value)
            ? 'las la-exclamation-circle icon-medium'
            : ''
        }))
        return acc
      },
      {} as Record<string, OptionType[]>
    )
    setMappedSyncFieldsOptions(prev => {
      const prevJSON = JSON.stringify(prev)
      const nextJSON = JSON.stringify(newSyncOptions)
      return prevJSON === nextJSON ? prev : newSyncOptions
    })
  }, [mappedSyncFieldsValues, mappedSyncFieldsOptions])

  const fieldsWithDataMatrix = useMemo(
    () => findAllFieldsPaths(fields, f => !!f.dataMatrixFields),
    [fields]
  )

  const allDataMatrixRefs = Array.from(
    new Set(
      fieldsWithDataMatrix.flatMap(fieldPath => {
        const field = findFieldByPath(fields, fieldPath)
        return field?.dataMatrixFields ?? []
      })
    )
  )

  const watchedDataMatrixFields = useWatch({
    control,
    name: allDataMatrixRefs
  })

  const mappedDataMatrixValues = allDataMatrixRefs.reduce(
    (acc, name, i) => {
      acc[name] = watchedDataMatrixFields[i]
      return acc
    },
    {} as Record<string, any>
  )

  /** What a field shows for its error. */
  const errorText = (message?: string) =>
    message && errorMessage ? errorMessage(message) : message

  /** A field with its `hint` under it, or the field alone. */
  const withHint = (field: FormFieldConfig, node: ReactElement) =>
    field.hint ? (
      <div className={styles.fieldWithHint}>
        {node}
        <p className={`body-small ${styles.fieldHint}`}>{field.hint}</p>
      </div>
    ) : (
      node
    )

  const renderField = (field: FormFieldConfig, parent = '') => {
    const fullName = parent ? `${parent}.${field.name}` : field.name

    if (
      ['text', 'email', 'password', 'text-area', 'number'].includes(
        field.type
      ) &&
      !!field.syncFormField
    ) {
      const syncFormFieldValue =
        mappedSyncFormFieldsValues[field.syncFormField]?.value ??
        methods.getValues(field.syncFormField)?.value

      let disabled = false
      if (isFormTranslating) {
        disabled = true
      } else if (field.disabled) {
        disabled = true
      } else if (
        field.referenceField &&
        field.referenceTypes?.includes('disableIfEmpty') &&
        mappedReferencedFieldsValues
      ) {
        const referenceFieldValue =
          mappedReferencedFieldsValues[field.referenceField]
        disabled = referenceFieldValue?.length === 0
      } else if (
        field.referenceField &&
        field.referenceTypes?.includes('disableIfNotEmpty') &&
        mappedReferencedFieldsValues
      ) {
        const referenceFieldValue =
          mappedReferencedFieldsValues[field.referenceField]
        disabled = referenceFieldValue && referenceFieldValue.length > 0
      }

      if (disabled && !!methods.getFieldState(fullName).error) {
        queueMicrotask(() => methods.clearErrors(fullName))
      }

      return (
        <Controller
          key={`${fullName}.${syncFormFieldValue}`}
          name={`${fullName}.${syncFormFieldValue}`}
          control={control}
          defaultValue=""
          render={({ field: f, fieldState }) => (
            <TextField
              {...f}
              value={
                typeof f.value === 'string' || typeof f.value === 'number'
                  ? f.value
                  : (f.value?.label ?? '')
              }
              label={
                isCondensed && fieldState.error?.message
                  ? errorText(fieldState.error.message)
                  : field.label
              }
              placeholder={field.placeholder}
              type={field.type as TextFieldType}
              hasError={!!fieldState.error}
              helperText={
                !condensed && !superCondensed
                  ? errorText(fieldState.error?.message) || ' '
                  : ''
              }
              multiline={field.type === 'text-area'}
              lines={field.lines}
              focused={field.focused}
              disabled={disabled}
              required={field.required}
              endAdornment={field.endAdornment}
              onlyInteger={field.onlyInteger}
            />
          )}
        />
      )
    }

    if (
      ['text', 'email', 'password', 'text-area', 'number'].includes(
        field.type
      ) &&
      !field.syncFormField
    ) {
      let disabled = false
      // Same rule the `syncFormField` branch above already applies: while the
      // automatic translation is filling these in, they are not typeable. The
      // label stays lifted because the config marks them `focused`, so the
      // incoming text does not land under it.
      if (isFormTranslating) {
        disabled = true
      } else if (field.disabled) {
        disabled = true
      } else if (
        field.referenceField &&
        field.referenceTypes?.includes('disableIfEmpty') &&
        mappedReferencedFieldsValues
      ) {
        const referenceFieldValue =
          mappedReferencedFieldsValues[field.referenceField]
        disabled = referenceFieldValue.length === 0
      } else if (
        field.referenceField &&
        field.referenceTypes?.includes('disableIfNotEmpty') &&
        mappedReferencedFieldsValues
      ) {
        const referenceFieldValue =
          mappedReferencedFieldsValues[field.referenceField]
        disabled = referenceFieldValue && referenceFieldValue.length > 0
      }

      if (disabled && !!methods.getFieldState(fullName).error) {
        queueMicrotask(() => methods.clearErrors(fullName))
      }

      return (
        <ConditionalField
          watchName={field.referenceField ?? ''}
          when={(values: OptionType[]) => {
            if (field.visibilityLogic) {
              return field.visibilityLogic === 'visibleIfEmpty'
                ? !values.length
                : values.length > 0
            }
            return true
          }}
          name={fullName}
        >
          <Controller
            key={fullName}
            name={fullName}
            control={control}
            defaultValue=""
            render={({ field: f, fieldState }) =>
              withHint(
                field,
                <TextField
                  {...f}
                  value={
                    typeof f.value === 'string' || typeof f.value === 'number'
                      ? f.value
                      : (f.value?.label ?? '')
                  }
                  label={
                    isCondensed && fieldState.error?.message
                      ? errorText(fieldState.error.message)
                      : field.label
                  }
                  placeholder={field.placeholder}
                  type={field.type as TextFieldType}
                  hasError={!!fieldState.error}
                  helperText={
                    !condensed && !superCondensed
                      ? errorText(fieldState.error?.message) || ' '
                      : ''
                  }
                  multiline={field.type === 'text-area'}
                  lines={field.lines}
                  focused={field.focused}
                  disabled={disabled}
                  required={field.required}
                  endAdornment={field.endAdornment}
                  onlyInteger={field.onlyInteger}
                />
              )
            }
          />
        </ConditionalField>
      )
    }

    if (field.type === 'select' || field.type === 'multi-select') {
      let options = field.options || []
      if (field.rawOptions) {
        const mappedRawOptions = field.rawOptions.map(rawOption => ({
          label:
            rawOption.translations.find(
              translation =>
                translation.languageCode ===
                (field.syncFormField
                  ? mappedSyncFormFieldsValues[field.syncFormField]?.value
                  : undefined)
            )?.name || '',
          value: rawOption.id
        }))
        options = mappedRawOptions
      } else if (
        field.isSyncFormField &&
        mappedSyncFieldsOptions[field.name]?.length
      ) {
        options = mappedSyncFieldsOptions[field.name] || []
      } else if (
        field.referenceField &&
        field.referenceTypes?.some(type => type === 'watchOptions') &&
        mappedReferencedFieldsValues
      ) {
        options = mappedReferencedFieldsValues[field.referenceField] || []
      }

      let disabled = false
      if (field.disabled) {
        disabled = true
      } else if (
        field.referenceField &&
        field.referenceTypes?.some(type => type === 'disableIfEmpty') &&
        mappedReferencedFieldsValues
      ) {
        const referenceFieldValue =
          mappedReferencedFieldsValues[field.referenceField]
        disabled = !referenceFieldValue || referenceFieldValue.length === 0
      } else if (
        field.referenceField &&
        field.referenceTypes?.includes('disableIfNotEmpty') &&
        mappedReferencedFieldsValues
      ) {
        const referenceFieldValue =
          mappedReferencedFieldsValues[field.referenceField]
        disabled = referenceFieldValue && referenceFieldValue.length > 0
      }

      if (disabled && !!methods.getFieldState(fullName).error) {
        queueMicrotask(() => methods.clearErrors(fullName))
      }

      // The referenced field's own pick, kept selected here. Read through the
      // same watched map the behaviours above use, so it follows the other
      // field live rather than settling on whatever was loaded.
      const lockedValues =
        field.referenceField &&
        field.referenceTypes?.includes('lockReferenceValue') &&
        mappedReferencedFieldsValues
          ? [
              (
                mappedReferencedFieldsValues[
                  field.referenceField
                ] as OptionType | null
              )?.value
            ].filter((value): value is string => !!value)
          : undefined

      const hasOptionsError = options.some(
        option => option.icon === 'las la-exclamation-circle icon-medium'
      )

      return (
        <Controller
          key={fullName}
          name={fullName}
          control={control}
          defaultValue={
            field.defaultValue || field.type === 'multi-select' ? [] : null
          }
          render={({ field: f, fieldState }) => {
            let customValue = f.value
            /*
              Drops a single select's pick once it is gone from the list it was
              picked out of.

              Narrow on purpose. It only makes sense for `watchOptions`, where
              the referenced field holds this one's options; a reference used
              for anything else points at a value of a shape this cannot read,
              and `.some` on a single option threw. The array check is the
              second guard, since the referenced field can also be momentarily
              undefined while the form is loading.
            */
            const referencedOptions = field.referenceField
              ? mappedReferencedFieldsValues?.[field.referenceField]
              : undefined

            if (
              field.type === 'select' &&
              field.referenceTypes?.includes('watchOptions') &&
              Array.isArray(referencedOptions) &&
              !referencedOptions.some(
                (option: OptionType) =>
                  option.value === (f.value as OptionType | null)?.value
              )
            ) {
              customValue = null
            }
            if (field.rawOptions) {
              const newValue: OptionType[] = field.rawOptions
                .filter((rawOption: RawOptionType) =>
                  (f.value as OptionType[]).some(
                    (option: OptionType) => option.value === rawOption.id
                  )
                )
                .map(rawOption => ({
                  value: rawOption.id,
                  label:
                    rawOption.translations.find(
                      translation =>
                        translation.languageCode ===
                        mappedSyncFormFieldsValues[field.syncFormField ?? '']
                          ?.value
                    )?.name || ''
                }))

              customValue = newValue
            }

            return withHint(
              field,
              <Select
                {...f}
                key={field.key}
                value={customValue}
                label={
                  isCondensed && fieldState.error?.message
                    ? errorText(fieldState.error.message)
                    : field.label
                }
                placeholder={field.placeholder}
                hasError={!!fieldState.error || hasOptionsError}
                helperText={
                  !condensed && !superCondensed
                    ? errorText(fieldState.error?.message) || ' '
                    : ''
                }
                options={options}
                focused={field.focused}
                isMutiSelect={field.type === 'multi-select'}
                hideClearIcon={field.hideClearIcon}
                fitTags={field.fitTags}
                lockedValues={lockedValues}
                disabled={disabled}
                defaultValue={field.defaultValue as selectValueType | undefined}
                required={field.required}
              />
            )
          }}
        />
      )
    }

    if (field.type === 'checkbox') {
      return (
        <Controller
          key={fullName}
          name={fullName}
          control={control}
          defaultValue={false}
          render={({ field: f }) => (
            <CheckBox field={f} label={field.label} disabled={field.disabled} />
          )}
        />
      )
    }

    if (field.type === 'checkboxGroup') {
      const checkBoxes = field.checkBoxes || []
      return (
        <Controller
          key={fullName}
          name={fullName}
          control={control}
          defaultValue={new Array(checkBoxes.length).fill(false)}
          render={({ field: f }) => (
            <CheckBoxGroup
              field={f as any}
              checkBoxes={checkBoxes}
              disabled={field.disabled}
            />
          )}
        />
      )
    }

    if (field.type === 'table') {
      return (
        <DynamicFormTableField
          key={`${fullName}-${JSON.stringify(mappedDataMatrixValues)}`}
          name={fullName}
          field={field}
          methods={methods}
          mappedDataMatrixValues={mappedDataMatrixValues}
          fullName={fullName}
        />
      )
    }

    if (field.type === 'input-group') {
      const groupClassName = `${styles.inputGroup} ${
        field.inputGroupDirection === 'column' ? styles.columnDirection : ''
      } ${field.scrollable ? styles.scrollableGroup : ''} ${
        field.gridColumns ? styles.gridGroup : ''
      }`
      // The column count is the one computed value, so it is the one inline.
      const groupStyle = {
        ...(field.alignItems && { alignItems: field.alignItems }),
        ...(field.gridColumns && {
          '--grid-columns': field.gridColumns
        })
      } as CSSProperties
      const spanClass = (input: FormFieldConfig) =>
        input.span ? styles[`span-${input.span}`] : ''
      const groupFields = field.inputs?.map((input, i) => (
        <div
          key={`${fullName}.${input.name || i}`}
          className={`${styles.inputGroupFieldWrapper} ${spanClass(input)}`}
          style={
            input.flexGrow !== undefined
              ? { flex: input.flexGrow === 0 ? 'none' : input.flexGrow }
              : undefined
          }
        >
          {renderField(input, fullName)}
        </div>
      ))

      if (field.heading) {
        return (
          <div key={fullName} className={styles.headedGroup}>
            <p className={`title-bold-small ${styles.groupHeading}`}>
              {field.heading}
            </p>
            <div className={groupClassName} style={groupStyle}>
              {groupFields}
            </div>
          </div>
        )
      }

      if (field.scrollable) {
        return (
          <ScrollableInputGroup
            key={fullName}
            className={groupClassName}
            style={groupStyle}
          >
            {groupFields}
          </ScrollableInputGroup>
        )
      }

      return (
        <div key={fullName} className={groupClassName} style={groupStyle}>
          {groupFields}
        </div>
      )
    }

    if (field.type === 'read-only') {
      return (
        <Fragment key={fullName}>
          {withHint(
            field,
            <ReadOnlyValue
              control={control}
              name={fullName}
              label={field.label}
              endAdornment={field.endAdornment}
            />
          )}
        </Fragment>
      )
    }

    if (field.type === 'custom') {
      return <Fragment key={fullName}>{field.render?.(methods)}</Fragment>
    }

    if (field.type === 'label') {
      return (
        <p key={fullName} className={`body-medium ${styles.formLabel}`}>
          {field.label}
        </p>
      )
    }

    if (field.type === 'image') {
      const shape = field.imageShape || 'rectangle'
      const isCircle = shape === 'circle'
      const storedImage = field.imageQuery?.data
      // Two DTOs answer with the same picture under different names: a dish
      // image is `url` (DishImageDto), the restaurant logo and background are
      // `imageUrl` (ImageDto). Reading only one left the saved dish photo
      // invisible in the form, so it looked like it could not be changed.
      const storedImageUrl = storedImage?.url ?? storedImage?.imageUrl
      return (
        <div className="imageField" key={fullName}>
          <ImageUploadButton
            imageShape={shape}
            label={field.label || t('DYNAMIC_FORM.BACKGROUND')}
            slotId={field.modalId || IMAGE_SLOTS_KEYS.DEFAULT_KEY}
            imageUrl={
              storedImageUrl
                ? `${storedImageUrl}?t=${storedImage?.version}&alt=media`
                : ''
            }
            onDelete={field.onImageDelete}
            imageSize={
              isCircle
                ? { width: 200, height: 200 }
                : { width: 608, height: 376 }
            }
            stencilSize={
              isCircle
                ? { width: 200, height: 200 }
                : { width: 432, height: 200 }
            }
          />
        </div>
      )
    }

    if (field.type === 'placeholder') {
      return (
        <ConditionalField
          watchName={field.referenceField ?? ''}
          when={(values: OptionType[]) => {
            if (field.visibilityLogic) {
              return field.visibilityLogic === 'visibleIfEmpty'
                ? !values.length
                : values.length > 0
            }
            return true
          }}
        >
          <div className={styles.formPlaceholder} />
        </ConditionalField>
      )
    }

    if (field.type === 'tagInputsList') {
      return (
        <div className={styles.tagInputsList}>
          {field.availableOptions?.map((availableOption: OptionType) => (
            <ConditionalField
              key={`${fullName}.${availableOption.value}`}
              watchName={field.referenceField ?? ''}
              when={(values: OptionType[]) =>
                !!values.find(value => value.value === availableOption.value)
              }
              name={`${fullName}.${availableOption.value}`}
            >
              <Controller
                key={`${fullName}.${availableOption.label}`}
                name={`${fullName}.${availableOption.value}`}
                control={control}
                defaultValue=""
                render={({ field: f, fieldState }) => (
                  <TagInput
                    {...f}
                    hasError={!!fieldState.error}
                    label={availableOption.label}
                  />
                )}
              />
            </ConditionalField>
          ))}
        </div>
      )
    }

    if (field.type === 'time') {
      return (
        <Controller
          key={fullName}
          name={fullName}
          control={control}
          defaultValue=""
          render={({ field: f, fieldState }) => (
            <TimePicker
              label={field.label || ''}
              // Kept as "HH:mm" in the form: a dayjs object in form state would
              // make every default-value comparison and every payload a special
              // case. The picker is the only thing that needs the object.
              value={
                f.value ? dayjs(f.value as string, TIME_VALUE_FORMAT) : null
              }
              onChange={value =>
                f.onChange(value ? value.format(TIME_VALUE_FORMAT) : '')
              }
              minutesStep={field.minutesStep ?? 15}
              disabled={field.disabled}
              // The config key text fields already use for "keep the label
              // visible"; a picker gets there by pinning the label rather than
              // by faking focus, see TimePicker.
              pinnedLabel={field.focused}
              hasError={!!fieldState.error}
              helperText={errorText(fieldState.error?.message) || ''}
            />
          )}
        />
      )
    }

    if (field.type === 'rating') {
      return (
        <div className="ratingField" key={fullName}>
          {/*
            Through a Controller like every other field. Rendered bare it kept
            its value in its own state, so the form never saw it: the dish
            payload read `formData.spiceLevel`, which was always undefined, and
            sent null every time.
          */}
          <Controller
            name={fullName}
            control={control}
            defaultValue={null}
            render={({ field: f }) => (
              <Rating
                icon={field.icon}
                max={field.max}
                precision={field.precision}
                label={field.label}
                value={typeof f.value === 'number' ? f.value : null}
                onChange={f.onChange}
              />
            )}
          />
        </div>
      )
    }

    if (field.type === 'tabs') {
      const tabs = field.tabs || []

      const defaultOpt = tabs[0]
        ? ({ label: tabs[0].label, value: tabs[0].value } as OptionType)
        : null

      // Find fields that use this tabs field as syncFormField
      const syncedFieldNames = fieldsWithSyncData.filter(fieldPath => {
        const syncField = findFieldByPath(fields, fieldPath)
        return syncField?.syncFormField === field.name
      })

      // Get error tabs from formState.errors for synced fields
      const errorTabsSet = new Set<string | number>()
      syncedFieldNames.forEach(fieldName => {
        const fieldErrors = get(formState.errors, fieldName) as Record<
          string,
          unknown
        > | null
        if (fieldErrors && typeof fieldErrors === 'object') {
          Object.keys(fieldErrors).forEach(langKey => {
            if (fieldErrors[langKey]) {
              errorTabsSet.add(langKey)
            }
          })
        }
      })
      const errorTabs = Array.from(errorTabsSet)

      return (
        <Controller
          key={fullName}
          name={fullName}
          control={control}
          defaultValue={defaultOpt}
          render={({ field: f }) => {
            const currentOpt = (f.value as OptionType) ?? defaultOpt
            const currentValue = currentOpt?.value ?? tabs[0]?.value

            const tabsField = (
              <Tabs
                tabs={tabs}
                value={currentValue}
                onChange={(v: number | string) => {
                  const tab = tabs.find(t => t.value === v)
                  f.onChange(
                    tab
                      ? ({ label: tab.label, value: tab.value } as OptionType)
                      : null
                  )
                }}
                additionalPadding
                errorTabs={errorTabs}
              />
            )

            return tabsField
          }}
        />
      )
    }

    return null
  }

  const handleSubmitAndReset = handleSubmit(
    async data => {
      setFormErrorMessageState(null)
      setShowFormSuccess(false)

      try {
        setIsSubmitted(true)

        if (onSubmit) {
          await onSubmit(data)
        }
        setShowFormSuccess(true)
        if (resetOnSubmit) {
          reset()
        }
      } catch (error: any) {
        setIsSubmitted(true)

        setFormErrorMessageState(t('DETAILS_MODAL.COMMON_FORM_ERROR_MESSAGE'))
        if (resetOnSubmit) {
          reset()
        }
      }
    },
    errors => {
      setIsSubmitted(true)

      setShowFormSuccess(false)
      setFormErrorMessageState(formErrorMessage)
      onInvalid?.(errors)

      const errorEntries = Object.entries(errors)

      const errorKeys = errorEntries
        .filter(([fieldPath, _fieldError]) => {
          const isFieldWithSyncData = fieldsWithSyncData.some(
            fieldWithSyncData => fieldWithSyncData === fieldPath
          )
          return isFieldWithSyncData
        })
        .map(([_fieldPath, fieldError]) => {
          const fieldErrorKeys = fieldError ? Object.keys(fieldError) : []
          return fieldErrorKeys
        })

      const flatErrorKeys = [...new Set(errorKeys.flat())]

      const newSyncFormFieldsOptions = syncFormFields.reduce(
        (acc, name) => {
          const field = findFieldByPath(fields, name)
          if (field && field.options) {
            acc[name] = field.options.map(option => ({
              ...option,
              icon: flatErrorKeys.includes(option.value)
                ? 'las la-exclamation-circle icon-medium'
                : ''
            }))
          }
          return acc
        },
        {} as Record<string, OptionType[]>
      )

      setMappedSyncFieldsOptions(newSyncFormFieldsOptions)
    }
  )

  return (
    <FormProvider {...methods}>
      <form
        className={`${
          !condensed && !superCondensed ? styles.dynamicForm : ''
        } ${condensed ? styles.dynamicFormCondensed : ''} ${
          superCondensed ? styles.dynamicFormSuperCondensed : ''
        } ${hasScrollableField ? styles.formFillsHeight : ''} ${
          sectioned ? styles.sectioned : ''
        }`}
        id={id}
        onSubmit={handleSubmitAndReset}
      >
        {visibleSteps.length > 1 && (
          <Stepper
            setActiveStep={step => {
              if (
                setActiveStep &&
                typeof step === 'number' &&
                step < visibleSteps.length
              ) {
                setActiveStep(step)
              }
            }}
            activeStep={activeStep}
            steps={visibleSteps}
            hasStepError={hasStepError}
            isSubmitted={isSubmitted}
          />
        )}

        {/*
          On every step, not just the first. The auto-translate banner is what
          this slot carries in six of its seven users, and translating is a
          whole-form operation: in the dish and topping modals it was started on
          the step that holds the language tabs, and moving on took the "in
          progress" message away and meant the "finished" one was never seen.
          The single-step modals showed both all along, which is the behaviour
          being matched here.
        */}
        {FormEffects}
        {formErrorMessageState && (
          <Alert
            severity="error"
            message={formErrorMessageState}
            onClose={() => setFormErrorMessageState(null)}
          />
        )}
        {showFormSuccess && formSuccessMessage && (
          <Alert
            severity="success"
            message={formSuccessMessage}
            onClose={() => setShowFormSuccess(false)}
          />
        )}
        {currentStepFields.map((field, index) => (
          <div
            key={`${field.name}-${index}`}
            className={
              field.scrollable ? styles.scrollableFieldSlot : undefined
            }
          >
            {renderField(field)}
          </div>
        ))}
        {FormActions}
      </form>
    </FormProvider>
  )
}

export default DynamicForm
